import { CheckException, ExceptionDecisionStatus } from '@treasury/domain/arp';
import { TmBaseComponent } from '@treasury/presentation';
import { PropertyValueMap, html } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import '../../components/jhd-approval-toggle';
import { JhaApprovalToggleStatus } from '../types';

export const tagName = 'ach-decision-toggle';
@customElement(tagName)
export class AchDecisionToggle extends TmBaseComponent {
    @property({ type: Object })
    public exception?: CheckException;

    @property({ type: Boolean, reflect: true, attribute: 'readonly' })
    public readonly = false;

    @state()
    private decision: JhaApprovalToggleStatus = JhaApprovalToggleStatus.NA;

    public render() {
        return html`<jhd-approval-toggle
            .selectedState=${this.decision}
            class="h-full"
            ?readonly=${this.readonly}
            @change=${(changeEvent: CustomEvent<JhaApprovalToggleStatus>) => {
                const newStatus = changeEvent.detail;
                if (newStatus === this.decision) {
                    return;
                }
                if (newStatus === JhaApprovalToggleStatus.Approved) {
                    this.payException();
                } else if (newStatus === JhaApprovalToggleStatus.Rejected) {
                    this.returnException();
                } else {
                    this.clearDecision();
                }
                this.dispatchEvent(new Event('change'));
            }}
        ></jhd-approval-toggle>`;
    }

    protected updated(
        _changedProperties: PropertyValueMap<AchDecisionToggle> | Map<PropertyKey, unknown>
    ): void {
        if (_changedProperties.has('exception') && this.exception) {
            if (this.exception.decisionStatus === ExceptionDecisionStatus.Pay) {
                this._payException();
            } else if (this.exception.decisionStatus === ExceptionDecisionStatus.Return) {
                this._returnException();
            } else {
                this._clearDecision();
            }
        }
    }

    private payException() {
        if (!this.exception) {
            throw new Error('Could not pay exception. One was not defined.');
        }
        this.exception.pay();
        this._payException();
        this.dispatchEvent(new CustomEvent<CheckException>('change', { detail: this.exception }));
    }

    private _payException() {
        this.decision = JhaApprovalToggleStatus.Approved;
    }

    private returnException() {
        if (!this.exception) {
            throw new Error('Could not return exception. One was not defined.');
        }
        this.exception.return();
        this._returnException();
        this.dispatchEvent(new CustomEvent<CheckException>('change', { detail: this.exception }));
    }

    private _returnException() {
        this.decision = JhaApprovalToggleStatus.Rejected;
    }

    private clearDecision() {
        if (!this.exception) {
            throw new Error('Could not clear exception. One was not defined.');
        }
        this.exception.clearDecision();
        this._clearDecision();
        this.dispatchEvent(new CustomEvent<CheckException>('change', { detail: this.exception }));
    }

    private _clearDecision() {
        this.decision = JhaApprovalToggleStatus.NA;
    }

    private acknowledgeException() {
        if (!this.exception) {
            throw new Error('Could not acknowledge exception. One was not defined.');
        }
        this.exception.acknowledged = !this.exception.acknowledged;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AchDecisionToggle;
    }
}
